import { useCallback, useEffect, useRef, useId } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useBowlMutation, useFencedState as useState } from '../../lib/bowlMutationContext.jsx'
import { api } from '../../lib/api.js'
import {
  rowControls, progressLine, quoteHref, artHref, emailTemplate, copyProblem, stepProblem, pinnedReason,
} from '../../lib/bowlWork.js'
import SchoolTeamPicker from './SchoolTeamPicker.jsx'
import ReadinessNotice from './ReadinessNotice.jsx'
import { readinessOf, assignmentSetupHref } from '../../lib/assignmentSetup.js'
import RepGmailConnect from '../champion-to-close/RepGmailConnect.jsx'
import styles from './bowlActions.module.css'

/**
 * EVERY BOWL TEAM ROW, ACTIONABLE.
 *
 * Brian, 2026-10-26: "in the bowl eligibility tab - there is no way to control
 * any of it - like send an email or assign a rep when youve got 3 listed."
 *
 * The row offers the same four moves as the championship board, wired to the
 * flows that already exist, plus the fix for whatever is blocking them:
 *
 *   Pick     the desk pick (current canPick authority, server enforced), offered whenever more
 *            than one rep is listed; ORDERED when nobody is accountable.
 *   Email    a draft in the rep's own Gmail through the shared rep send path.
 *   Art      Artwork Studio's new-job flow, then "mark art sent".
 *   Quote    the pricing engine pre-filled, then "mark quote sent".
 *   Win      the order named, which clears the team from the worklist.
 *   Fixes    confirm the school account (exec), add a contact (desk), assign
 *            a desk (Governing Bodies). No flag without its control.
 *
 * ⭐ IT DRAWS THE SERVER'S DECISION. `team.work` is the backend's teamWork(),
 * the same function the worklist orders come from, so this row and a rep's
 * worklist cannot disagree. Every write is re-decided by the server.
 */
export default function BowlTeamActions({ team, season, isExec = false, viewerIsExec = isExec, canPick = false, viewerName = '', actorId = '', deskVersion = null, workMeta = null, onChanged, focus = '', rowLocation = 'group' }) {
  const work = team?.work || null
  const name = team?.name || team?.team || ''
  const [open, setOpen] = useState('')
  const [said, setSaid] = useState(null)
  const [, setParams] = useSearchParams()
  const location = useLocation()
  const currentNavigation = useRef(location.key)
  currentNavigation.current = location.key
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const reasonId = useId()
  const navigateAction = useCallback((next, { replace = false } = {}) => {
    setOpen(next)
    setParams(previous => {
      const query = new URLSearchParams(previous)
      query.set('team', name)
      if (rowLocation === 'trigger') query.set('actionRow', 'trigger'); else query.delete('actionRow')
      if (next) query.set('do', next); else query.delete('do')
      return query
    }, { replace })
  }, [name, rowLocation, setOpen, setParams])

  /* Arriving from a worklist order (?team=X&do=email) opens that panel AND
   * brings the row to the top of the phone screen. Measured at 390px: one
   * scroll at mount was undone by the sections above still loading (trigger
   * list, funnel) and the rep landed on the page header with the composer far
   * below. So it re-anchors as those settle. */
  const wrapRef = useRef(null)
  useEffect(() => {
    if (!focus) { setOpen(''); return undefined }
    setOpen(focus === 'step' ? (work?.nextStep || '') : focus)
    const anchor = () => (wrapRef.current?.closest('tr') || wrapRef.current)?.scrollIntoView({ block: 'start' })
    const timers = [0, 400, 1200, 2500].map((ms) => setTimeout(anchor, ms))
    return () => timers.forEach(clearTimeout)
  }, [focus]) // eslint-disable-line react-hooks/exhaustive-deps

  const done = useCallback((text) => {
    setSaid({ ok: true, text })
    // Closing a pending form does not cancel its request. A late receipt must
    // not take the user away from another team's subsequently opened action.
    if (mounted.current && currentNavigation.current === location.key) navigateAction('', { replace: true })
    onChanged?.()
  }, [location.key, navigateAction, onChanged, setSaid])

  const teamEditor = canPick && <SchoolTeamPicker school={name} conference={team.conference} season={season} actorId={actorId}
    expectedVersion={deskVersion} members={team.coverage?.members || []} defaultOpen={open === 'pick_rep'}
    onSaved={() => done('School team saved and the board is refreshing.')} />
  if (!work) {
    return <div className={styles.wrap} data-bowl-actions={name} data-bowl-row={rowLocation} ref={wrapRef}>{teamEditor}<p className={styles.bad}>Selling actions could not be read. Reload the board.</p></div>
  }
  const setupHref = assignmentSetupHref(team.coverage?.body || team.conference, `/bowl-eligibility?${new URLSearchParams({ team: name, do: open || focus || '', ...(rowLocation === 'trigger' ? { actionRow: 'trigger' } : {}) })}`)
  const { controls, fixes, mine } = rowControls(work, { isExec, viewerIsExec, canPick, viewerName, viewerId:actorId, setupHref })
  const panelScope = `${name}:${work.account?.companyId || 'none'}:${workMeta?.version ?? 'unreadable'}`
  const readiness = readinessOf(workMeta, { requireVersion: true })
  const ready = readiness.state === 'ready'
  const toggle = (k) => {
    setSaid(null)
    navigateAction(open === k ? '' : k)
  }
  // URL navigation cannot open a write form that its visible button refuses.
  const panelAllowed = open === 'pick_rep' && canPick || ready && ([...controls, ...fixes].some(c => c.key === open && c.enabled)
    || open === 'find_contact' && mine && !!work.account)
  const blockedReason = !ready
    ? readiness.state === 'setup' ? 'Actions are paused until the authoritative assignment roster is set up.'
      : readiness.state === 'pending' || readiness.pending ? 'An operation needs reconciliation. Inspect saved history; do not repeat it.'
        : 'The current action state could not be read. Refresh the board before making changes.'
    : viewerIsExec && !isExec && !mine ? 'Current action permission is unavailable. Refresh the board or ask an administrator to check access.' : ''

  return (
    <div className={styles.wrap} data-bowl-actions={name} data-bowl-row={rowLocation} ref={wrapRef}>
      {teamEditor}
      {/* Post-V4.001: a missing assignment roster is SETUP, not lost history. */}
      <ReadinessNotice readiness={readiness} isExec={viewerIsExec} compact setupHref={setupHref}
        classes={{ bad: styles.bad, setup: styles.setup, setupText: styles.setupText, btn: `${styles.btn} ${styles.btnFix}` }}
        unavailableText="Action history is unavailable. Refresh and inspect saved history before making changes."
        pendingText="An operation is pending or needs reconciliation: its outcome is not confirmed. Refresh and inspect saved history before making changes. Do not repeat it." />
      <p className={`${styles.progress} ${work.complete ? styles.won : ''}`}>{progressLine(work)}</p>
      <p id={reasonId} className={styles.say}>{blockedReason || 'Available actions follow your current desk and account permissions.'}</p>

      {fixes.length > 0 && (
        <div className={styles.fixes}>
          {fixes.map((f) => (
            <div key={f.key} className={styles.fix}>
              <p className={styles.fixWhy}>{f.why}</p>
              {f.kind === 'link' ? (
                <Link className={`${styles.btn} ${styles.btnFix}`} to={f.to}>{f.label}</Link>
              ) : (
                <button type="button" className={`${styles.btn} ${styles.btnFix}`} disabled={!f.enabled || f.key !== 'pick_rep' && !ready}
                  aria-describedby={reasonId} aria-expanded={panelAllowed && open === f.key} onClick={() => toggle(f.key)}>{f.label}</button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={styles.buttons}>
        {controls.map((c) => (
          <button key={c.key} type="button" disabled={!c.enabled || !ready} title={c.why || undefined}
            aria-describedby={reasonId} aria-expanded={panelAllowed && open === c.key}
            className={`${styles.btn} ${c.done ? styles.btnDone : ''} ${c.next ? styles.btnNext : ''}`}
            onClick={() => toggle(c.key)}>
            {c.done ? '✓ ' : ''}{c.label}
          </button>
        ))}
      </div>
      {ready && !blockedReason && controls.some((c) => !c.enabled && c.why) && !controls.every((c) => c.enabled) && (
        <p className={styles.say}>{[...new Set(controls.filter((c) => !c.enabled).map((c) => c.why))].join(' ')}</p>
      )}

      {panelAllowed && <button type="button" className={styles.btn} onClick={() => toggle(open)}>Close action</button>}
      {panelAllowed && open === 'confirm_account' && <AccountPanel key={panelScope} team={name} proposal={work.accountProposal || null} onDone={done} />}
      {panelAllowed && open === 'find_contact' && <ContactPanel key={panelScope} team={name} work={work} onDone={done} />}
      {panelAllowed && open === 'email' && (
        <EmailPanel key={panelScope} team={name} work={work} viewerName={viewerName}
          onDone={done} onNeedContact={() => navigateAction('find_contact')} />
      )}
      {panelAllowed && open === 'art' && <StepPanel key={`${panelScope}:${open}`} team={name} step="art" work={work} onDone={done} />}
      {panelAllowed && open === 'quote' && <StepPanel key={`${panelScope}:${open}`} team={name} step="quote" work={work} season={season} onDone={done} />}
      {panelAllowed && open === 'win' && <StepPanel key={`${panelScope}:${open}`} team={name} step="win" work={work} onDone={done} />}

      {said && <p className={said.ok ? styles.ok : styles.bad} role="status" aria-live="polite">{said.text}</p>}
    </div>
  )
}

/* The fence's own sentence wins for an uncertain write ("Needs reconciliation"
 * + what may have happened) and for a read failure, so neither can be
 * replaced by a server detail that reads like the ordinary setup refusal. */
const errText = (e, fallback) => (e?.code === 'uncertain_action' || e?.code === 'read_failure') && e?.message
  ? e.message
  : e?.body?.detail || e?.body?.reason || e?.body?.error || e?.message || fallback

/* ---- confirm the school account (exec) ------------------------------- */
function AccountPanel({ team, proposal = null, onDone }) {
  const mutate = useBowlMutation()
  const [opts, setOpts] = useState({ status: 'loading', data: null, error: '' })
  const [pick, setPick] = useState('')
  const [manual, setManual] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const c = new AbortController()
    api.fbsBowlAccountOptions(team, { signal: c.signal })
      .then((d) => {
        if (c.signal.aborted) return
        setOpts({ status: 'ready', data: d, error: '' })
        /* ⭐ A PINNED CANDIDATE IS PRESELECTED WITH ITS BASIS AS THE REASON
         * (2026-11-01), so the exec's confirm is one tap on a named record.
         * Only a pinned, unrefused candidate; the button is still the exec's. */
        const pinned = (d?.candidates || []).find((x) => x.pinned && !x.refused)
        if (pinned) { setPick(pinned.id); setReason((r) => r || pinnedReason(d.basis || pinned.name)) }
      })
      .catch((e) => { if (e?.name !== 'AbortError') setOpts({ status: 'error', data: null, error: errText(e, 'Could not read the candidates.') }) })
    return () => c.abort()
  }, [team])

  const submit = async (e) => {
    e.preventDefault()
    const companyId = (manual || pick).trim()
    if (!/^\d+$/.test(companyId)) { setErr('Choose a company or paste its HubSpot company id.'); return }
    if (reason.trim().length < 5) { setErr('Say why, in a sentence.'); return }
    setBusy(true); setErr('')
    try {
      const r = await mutate(api.fbsBowlAccount, { team, companyId, reason: reason.trim() }, { action: 'account' })
      onDone(`${team} is now ${r?.row?.companyName || 'the chosen company'}. The board will refresh its current actions.`)
    } catch (x) { setErr(errText(x, 'The account was not confirmed.')) } finally { setBusy(false) }
  }

  const cands = opts.data?.candidates || []
  return (
    <form className={styles.panel} onSubmit={submit} aria-label={`Confirm ${team}'s school account`}>
      <p className={styles.panelHead}>Which HubSpot company is {team}?</p>
      {proposal && <p className={styles.say}>Proposed: {proposal.basis || proposal.name}</p>}
      {opts.status === 'loading' && <p className={styles.say}>Reading the matcher…</p>}
      {opts.status === 'error' && <p className={styles.bad}>{opts.error}</p>}
      {opts.data?.institution && <p className={styles.say}>Matched on {opts.data.institution}{opts.data.state ? `, ${opts.data.state}` : ''}.</p>}
      {opts.status === 'ready' && !cands.length && <p className={styles.say}>No school candidates were returned. Check the school’s HubSpot company ID before confirming; no match has been inferred.</p>}
      {opts.data?.why && <p className={styles.say}>{opts.data.why}</p>}
      {cands.map((c) => (
        <label key={c.id} className={styles.choice}>
          <input type="radio" name={`acct-${team}`} value={c.id} disabled={!!c.refused}
            checked={pick === c.id} onChange={() => { setPick(c.id); setManual('') }} />
          <span>{c.name || c.id}{c.city || c.state ? ` · ${[c.city, c.state].filter(Boolean).join(', ')}` : ''}
            {c.orgType ? ` · ${c.orgType}` : ''}{c.refused ? ` (refused: ${c.refused})` : ''}</span>
        </label>
      ))}
      <label className={styles.label}>
        Or paste the company id
        <input className={styles.input} inputMode="numeric" value={manual}
          onChange={(e) => { setManual(e.target.value); setPick('') }} placeholder="e.g. 18294751023" />
      </label>
      <label className={styles.label}>
        Why (required)
        <input className={styles.input} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. UNT athletics record, checked in HubSpot" maxLength={300} />
      </label>
      {err && <p className={styles.bad} role="alert">{err}</p>}
      <div className={styles.row}>
        <button className={styles.btn} type="submit" disabled={busy || opts.status !== 'ready'}>{busy ? 'Saving…' : 'Confirm the account'}</button>
      </div>
      <p className={styles.say}>Recorded with your name and the time. It writes no HubSpot property.</p>
    </form>
  )
}

/* ---- add a contact (desk) -------------------------------------------- */
function ContactPanel({ team, work, onDone }) {
  const mutate = useBowlMutation()
  const [f, setF] = useState({ email: '', firstname: '', lastname: '', jobtitle: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }))
  const submit = async (e) => {
    e.preventDefault()
    if (!/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(f.email.trim())) { setErr('A usable email address is required.'); return }
    setBusy(true); setErr('')
    try {
      const result = await mutate(api.fbsBowlContact, { team, ...f, email: f.email.trim() })
      if (!result?.contactId) throw new Error('The saved action has no confirmed contact receipt. Inspect its history before another attempt.')
      onDone(`Contact added to ${work.account?.companyName || team}. Refreshing linked contacts before email.`)
    } catch (x) { setErr(errText(x, 'The contact was not added.')) } finally { setBusy(false) }
  }
  return (
    <form className={styles.panel} onSubmit={submit} aria-label={`Add a contact for ${team}`}>
      <p className={styles.panelHead}>Add a contact to {work.account?.companyName || team}</p>
      <label className={styles.label}>Email (required)
        <input className={styles.input} type="email" value={f.email} onChange={set('email')} /></label>
      <label className={styles.label}>First name
        <input className={styles.input} value={f.firstname} onChange={set('firstname')} /></label>
      <label className={styles.label}>Last name
        <input className={styles.input} value={f.lastname} onChange={set('lastname')} /></label>
      <label className={styles.label}>Title
        <input className={styles.input} value={f.jobtitle} onChange={set('jobtitle')} placeholder="e.g. Director of Football Operations" /></label>
      {err && <p className={styles.bad} role="alert">{err}</p>}
      <div className={styles.row}>
        <button className={styles.btn} type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add the contact'}</button>
      </div>
      <p className={styles.say}>Creates the contact in HubSpot and links it to the school account.</p>
    </form>
  )
}

/* ---- email: a draft in the rep's own Gmail --------------------------- */
function EmailPanel({ team, work, viewerName, onDone, onNeedContact }) {
  const mutate = useBowlMutation()
  const tpl = emailTemplate(team, work, viewerName)
  const [contacts, setContacts] = useState({ status: 'loading', contacts: [], reason: '' })
  const [to, setTo] = useState([])
  const [subject, setSubject] = useState(tpl.subject)
  const [body, setBody] = useState(tpl.body)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const c = new AbortController()
    api.fbsBowlContacts(team, { fresh: true, signal: c.signal })
      .then((d) => {
        if (c.signal.aborted) return
        setContacts({ status: d?.status || 'unavailable', contacts: d?.contacts || [], reason: d?.reason || '' })
        setTo(d?.status === 'ready' && d?.contacts?.[0]?.email ? [d.contacts[0].email] : [])
      })
      .catch((e) => { if (e?.name !== 'AbortError') setContacts({ status: 'unavailable', contacts: [], reason: errText(e, 'Contacts could not be read.') }) })
    return () => c.abort()
  }, [team])

  const toggleTo = (email) => setTo((xs) => (xs.includes(email) ? xs.filter((x) => x !== email) : [...xs, email]))
  const submit = async (e) => {
    e.preventDefault()
    if (contacts.status !== 'ready') { setErr('The linked contact list is not complete. Refresh and reconcile it before selecting recipients.'); return }
    const p = copyProblem({ subject, body })
    if (p) { setErr(p); return }
    if (!to.length) { setErr('Choose who it goes to.'); return }
    setBusy(true); setErr('')
    try {
      const r = await mutate(api.fbsBowlEmailDraft, { team, to, subject, body }, { action: 'email-draft' })
      if (!r?.draftId || r.sent !== false || (r.sharedAdmission === true && (r.requestedOperationId !== (r.reusedWorkRow === true ? r.workOperationRequestId : r.row?.requestId) || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(r.requestId || '')))) throw new Error('The saved action has no confirmed draft receipt. Inspect Gmail and the action history; do not repeat it blindly.')
      onDone(r?.whatHappensNext || `Draft saved in ${r?.mailbox || 'your Gmail'}. Open Gmail to send it; nothing was sent.`)
    } catch (x) { setErr(errText(x, 'No confirmed draft receipt. Inspect your Gmail and saved action history before retrying.')) } finally { setBusy(false) }
  }

  return (
    <form className={styles.panel} onSubmit={submit} aria-label={`Email ${team}`}>
      <p className={styles.panelHead}>Draft to {work.account?.companyName || team}</p>
      <RepGmailConnect />
      {contacts.status === 'loading' && <p className={styles.say}>Reading the contacts on the account…</p>}
      {contacts.status !== 'loading' && contacts.contacts.length === 0 && (
        <div className={styles.fix}>
          <p className={styles.fixWhy}>{contacts.reason || 'No contact with an email is on this account.'}</p>
          <button type="button" className={`${styles.btn} ${styles.btnFix}`} onClick={onNeedContact}>Add the contact</button>
        </div>
      )}
      {contacts.status === 'incomplete' && <p className={styles.bad}>{contacts.reason}</p>}
      {contacts.contacts.map((c) => (
        <label key={c.id} className={styles.choice}>
          <input type="checkbox" disabled={contacts.status !== 'ready'} checked={to.includes(c.email)} onChange={() => toggleTo(c.email)} />
          <span>{c.name}{c.title ? ` · ${c.title}` : ''} · {c.email}</span>
        </label>
      ))}
      <label className={styles.label}>Subject
        <input className={styles.input} value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
      <label className={styles.label}>Message
        <textarea className={`${styles.input} ${styles.textarea}`} value={body} onChange={(e) => setBody(e.target.value)} /></label>
      {err && <p className={styles.bad} role="alert">{err}</p>}
      <div className={styles.row}>
        <button className={styles.btn} type="submit" disabled={busy || contacts.status !== 'ready' || !contacts.contacts.length}>
          {busy ? 'Drafting…' : 'Draft in my Gmail'}
        </button>
      </div>
      <p className={styles.say}>A draft in your own mailbox. Nothing is sent from here; you send it from Gmail.</p>
    </form>
  )
}

/* ---- art / quote / win: open the existing flow, then record it ------- */
function StepPanel({ team, step, work, season, onDone }) {
  const mutate = useBowlMutation()
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const submit = async (e) => {
    e.preventDefault()
    const p = stepProblem(step, note)
    if (p) { setErr(p); return }
    setBusy(true); setErr('')
    try {
      await mutate(api.fbsBowlStep, { team, step, note: note.trim() })
      onDone(step === 'win' ? `Win recorded for ${team}. It leaves the worklist.` : `${step === 'art' ? 'Art' : 'Quote'} recorded for ${team}.`)
    } catch (x) { setErr(errText(x, 'That was not recorded.')) } finally { setBusy(false) }
  }
  const head = { art: `Ring art for ${team}`, quote: `Quote ${team}`, win: `Record the win for ${team}` }[step]
  const placeholder = { art: 'e.g. Bezel-drop proof r2 sent to the AD', quote: 'e.g. Quote SR-037100 sent', win: 'e.g. ClickUp order 86b0001' }[step]
  return (
    <form className={styles.panel} onSubmit={submit} aria-label={head}>
      <p className={styles.panelHead}>{head}</p>
      {step === 'art' && <><Link className={styles.btn} to={artHref(team)}>Open Artwork Studio</Link><p className={styles.say}>Opens the artwork directory. Choose Create artwork there to start a request. This button does not generate artwork.</p></>}
      {step === 'quote' && (
        <a className={styles.btn} href={quoteHref(season, team, work)} target="_blank" rel="noopener noreferrer">
          Open the pricing engine
        </a>
      )}
      <label className={styles.label}>
        {step === 'win' ? 'Order (required)' : 'What went out (required)'}
        <input className={styles.input} value={note} onChange={(e) => setNote(e.target.value)} placeholder={placeholder} maxLength={300} />
      </label>
      {err && <p className={styles.bad} role="alert">{err}</p>}
      <div className={styles.row}>
        <button className={styles.btn} type="submit" disabled={busy}>
          {busy ? 'Saving…' : step === 'win' ? 'Record the win' : `Mark ${step} sent`}
        </button>
      </div>
    </form>
  )
}
